// src/services/bookingsService.js

import { apiRequest } from "./apiService";

const DEFAULT_LIMIT = 25;
const MAX_LIMIT = 100;
const MAX_ID_LENGTH = 128;
const MAX_CURSOR_LENGTH = 4096;
const MAX_SERVICE_LENGTH = 160;
const MAX_NOTES_LENGTH = 1000;
const PAGE_SLUG_PATTERN = /^[a-z0-9-]{1,128}$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const REQUEST_TIMEOUT_MS = 15_000;

export const BOOKING_STATUS = Object.freeze({
  REQUESTED: "requested",
  CONTACTED: "contacted",
  CONFIRMED: "confirmed",
  DECLINED: "declined",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
  NO_SHOW: "no_show",
});

export const BOOKING_STATUS_LABEL = Object.freeze({
  [BOOKING_STATUS.REQUESTED]: "Awaiting response",
  [BOOKING_STATUS.CONTACTED]: "Contacted",
  [BOOKING_STATUS.CONFIRMED]: "Confirmed",
  [BOOKING_STATUS.DECLINED]: "Declined",
  [BOOKING_STATUS.CANCELLED]: "Cancelled",
  [BOOKING_STATUS.COMPLETED]: "Completed",
  [BOOKING_STATUS.NO_SHOW]: "No show",
});

export const BOOKING_ALLOWED_TRANSITIONS = Object.freeze({
  [BOOKING_STATUS.REQUESTED]: Object.freeze([
    BOOKING_STATUS.CONTACTED,
    BOOKING_STATUS.CONFIRMED,
    BOOKING_STATUS.DECLINED,
    BOOKING_STATUS.CANCELLED,
  ]),
  [BOOKING_STATUS.CONTACTED]: Object.freeze([
    BOOKING_STATUS.CONFIRMED,
    BOOKING_STATUS.DECLINED,
    BOOKING_STATUS.CANCELLED,
  ]),
  [BOOKING_STATUS.CONFIRMED]: Object.freeze([
    BOOKING_STATUS.COMPLETED,
    BOOKING_STATUS.NO_SHOW,
    BOOKING_STATUS.CANCELLED,
  ]),
  [BOOKING_STATUS.DECLINED]: Object.freeze([]),
  [BOOKING_STATUS.CANCELLED]: Object.freeze([]),
  [BOOKING_STATUS.COMPLETED]: Object.freeze([]),
  [BOOKING_STATUS.NO_SHOW]: Object.freeze([]),
});

const VALID_BOOKING_STATUSES = new Set(Object.values(BOOKING_STATUS));

const norm = (value) => String(value == null ? "" : value).trim();

const isPlainObject = (value) =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const unwrapResponseData = (payload) => {
  if (isPlainObject(payload?.data)) return payload.data;
  return payload;
};

const createValidationError = (message, code, status = 400) => {
  const error = new Error(message);
  error.name = "BookingValidationError";
  error.code = code;
  error.status = status;
  return error;
};

const requiredText = (
  value,
  fieldLabel,
  code,
  maxLength = MAX_ID_LENGTH,
) => {
  const text = norm(value);

  if (!text) {
    throw createValidationError(`${fieldLabel} is required.`, code);
  }

  if (text.length > maxLength) {
    throw createValidationError(
      `${fieldLabel} must be no more than ${maxLength} characters.`,
      code,
    );
  }

  return text;
};

const optionalText = (
  value,
  fieldLabel,
  code,
  maxLength = MAX_ID_LENGTH,
) => {
  const text = norm(value);
  if (!text) return "";

  if (text.length > maxLength) {
    throw createValidationError(
      `${fieldLabel} must be no more than ${maxLength} characters.`,
      code,
    );
  }

  return text;
};

const normalizeStatus = (value, { required = false } = {}) => {
  const status = norm(value).toLowerCase();

  if (!status) {
    if (required) {
      throw createValidationError(
        "Booking status is required.",
        "BOOKING_STATUS_REQUIRED",
      );
    }

    return "";
  }

  if (!VALID_BOOKING_STATUSES.has(status)) {
    throw createValidationError(
      "The selected booking status is invalid.",
      "INVALID_BOOKING_STATUS",
    );
  }

  return status;
};

const normalizeLimit = (value) => {
  if (value == null || value === "") return DEFAULT_LIMIT;

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_LIMIT) {
    throw createValidationError(
      `Limit must be between 1 and ${MAX_LIMIT}.`,
      "INVALID_BOOKING_LIMIT",
    );
  }

  return parsed;
};

const normalizePublicBookingRequest = (payload) => {
  if (!isPlainObject(payload)) {
    throw createValidationError(
      "Valid booking details are required.",
      "INVALID_BOOKING_REQUEST",
    );
  }

  const leadId = requiredText(
    payload.leadId ?? payload.lead_id,
    "Lead reference",
    "INVALID_LEAD_ID",
  );

  const serviceName = requiredText(
    payload.serviceName ?? payload.service_name,
    "Service",
    "INVALID_SERVICE_NAME",
    MAX_SERVICE_LENGTH,
  );

  const preferredDate = requiredText(
    payload.preferredDate ?? payload.preferred_date,
    "Preferred date",
    "INVALID_PREFERRED_DATE",
    10,
  );

  if (!DATE_PATTERN.test(preferredDate)) {
    throw createValidationError(
      "Choose a valid preferred date.",
      "INVALID_PREFERRED_DATE",
    );
  }

  const preferredTime = requiredText(
    payload.preferredTime ?? payload.preferred_time,
    "Preferred time",
    "INVALID_PREFERRED_TIME",
    5,
  );

  if (!TIME_PATTERN.test(preferredTime)) {
    throw createValidationError(
      "Choose a valid preferred time.",
      "INVALID_PREFERRED_TIME",
    );
  }

  const customerNotes = optionalText(
    payload.customerNotes ?? payload.customer_notes,
    "Notes",
    "INVALID_CUSTOMER_NOTES",
    MAX_NOTES_LENGTH,
  );

  return {
    leadId,
    serviceName,
    preferredDate,
    preferredTime,
    ...(customerNotes ? { customerNotes } : {}),
    ...(isPlainObject(payload.trackingContext)
      ? { trackingContext: payload.trackingContext }
      : {}),
  };
};

const normalizeBookingRecord = (value) => {
  const item = unwrapResponseData(value);

  if (!isPlainObject(item)) {
    throw createValidationError(
      "The booking response is invalid.",
      "INVALID_BOOKING_RESPONSE",
      502,
    );
  }

  return {
    ...item,
    bookingId: norm(item.bookingId ?? item.booking_id),
    campaignId: norm(item.campaignId ?? item.campaign_id),
    offerId: norm(item.offerId ?? item.offer_id),
    leadId: norm(item.leadId ?? item.lead_id),
    pageSlug: norm(item.pageSlug ?? item.page_slug),
    inviteId: norm(item.inviteId ?? item.invite_id),
    influencerId: norm(item.influencerId ?? item.influencer_id),
    customerName: norm(item.customerName ?? item.customer_name),
    customerEmail: norm(item.customerEmail ?? item.customer_email),
    customerPhone: norm(item.customerPhone ?? item.customer_phone),
    serviceName: norm(item.serviceName ?? item.service_name),
    preferredDate: norm(item.preferredDate ?? item.preferred_date),
    preferredTime: norm(item.preferredTime ?? item.preferred_time),
    customerNotes: norm(item.customerNotes ?? item.customer_notes),
    ownerNotes: norm(item.ownerNotes ?? item.owner_notes),
    ownerNotificationEmail: norm(
      item.ownerNotificationEmail ?? item.owner_notification_email,
    ),
    ownerNotificationStatus: norm(
      item.ownerNotificationStatus ??
        item.owner_notification_status ??
        item.notificationStatus ??
        item.notification_status,
    ),
    ownerNotificationSentAt: norm(
      item.ownerNotificationSentAt ?? item.owner_notification_sent_at,
    ),
    ownerNotificationError: norm(
      item.ownerNotificationError ?? item.owner_notification_error,
    ),
    status: normalizeStatus(item.status || BOOKING_STATUS.REQUESTED, {
      required: true,
    }),
    createdAt: norm(item.createdAt ?? item.created_at),
    updatedAt: norm(item.updatedAt ?? item.updated_at),
  };
};

export const canTransitionBookingStatus = (
  currentStatus,
  nextStatus,
) => {
  const current = normalizeStatus(currentStatus, { required: true });
  const next = normalizeStatus(nextStatus, { required: true });

  if (current === next) return true;

  return BOOKING_ALLOWED_TRANSITIONS[current]?.includes(next) === true;
};

export const createBookingIdempotencyKey = () => {
  const browserCrypto =
    typeof window !== "undefined" ? window.crypto : null;

  try {
    if (typeof browserCrypto?.randomUUID === "function") {
      return `booking-${browserCrypto.randomUUID()}`;
    }

    if (typeof browserCrypto?.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      browserCrypto.getRandomValues(bytes);

      return `booking-${Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join("")}`;
    }
  } catch {
    // Continue to the browser-compatible fallback.
  }

  return `booking-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 14)}`;
};

export async function createPublicBooking(
  pageSlug,
  payload,
  {
    idempotencyKey = createBookingIdempotencyKey(),
    signal,
  } = {},
) {
  const slug = requiredText(
    pageSlug,
    "Promotion link",
    "INVALID_PAGE_SLUG",
  ).toLowerCase();

  if (!PAGE_SLUG_PATTERN.test(slug)) {
    throw createValidationError(
      "The promotion link is invalid.",
      "INVALID_PAGE_SLUG",
    );
  }

  const body = normalizePublicBookingRequest(payload);
  const safeIdempotencyKey = requiredText(
    idempotencyKey,
    "Idempotency key",
    "INVALID_IDEMPOTENCY_KEY",
  );

  const response = await apiRequest(
    `/public/promotions/${encodeURIComponent(slug)}/bookings`,
    "POST",
    body,
    {
      requiresAuth: false,
      suppressAuthInvalidEvent: true,
      withCredentials: false,
      timeoutMs: 15_000,
      signal,
      extraHeaders: {
        "Idempotency-Key": safeIdempotencyKey,
      },
    },
  );

  const result = unwrapResponseData(response);

  if (!isPlainObject(result) || !norm(result.bookingId ?? result.booking_id)) {
    throw createValidationError(
      "The booking response is invalid.",
      "INVALID_PUBLIC_BOOKING_RESPONSE",
      502,
    );
  }

  return {
    ...result,
    bookingId: norm(result.bookingId ?? result.booking_id),
    status: normalizeStatus(
      result.status || BOOKING_STATUS.REQUESTED,
      { required: true },
    ),
    createdAt: norm(result.createdAt ?? result.created_at),
    message:
      norm(result.message) || "Booking request sent. The business will contact you to confirm availability. This is not a confirmed booking yet.",
    ownerNotified: result.ownerNotified === true || result.owner_notified === true,
    notificationStatus: norm(result.notificationStatus ?? result.notification_status),
    idempotent: result.idempotent === true,
  };
}

export async function listBookings(
  {
    campaignId,
    offerId,
    leadId,
    status,
    limit = DEFAULT_LIMIT,
    cursor,
  } = {},
  { signal } = {},
) {
  const params = new URLSearchParams();

  params.set(
    "campaignId",
    requiredText(
      campaignId,
      "Campaign reference",
      "INVALID_CAMPAIGN_ID",
    ),
  );
  params.set("limit", String(normalizeLimit(limit)));

  const safeOfferId = optionalText(
    offerId,
    "Promotion reference",
    "INVALID_OFFER_ID",
  );
  const safeLeadId = optionalText(
    leadId,
    "Lead reference",
    "INVALID_LEAD_ID",
  );
  const safeStatus = normalizeStatus(status);
  const safeCursor = optionalText(
    cursor,
    "Pagination cursor",
    "INVALID_CURSOR",
    MAX_CURSOR_LENGTH,
  );

  if (safeOfferId) params.set("offerId", safeOfferId);
  if (safeLeadId) params.set("leadId", safeLeadId);
  if (safeStatus) params.set("status", safeStatus);
  if (safeCursor) params.set("cursor", safeCursor);

  const response = unwrapResponseData(
    await apiRequest(`/bookings?${params.toString()}`, "GET", null, {
      requiresAuth: true,
      suppressAuthInvalidEvent: false,
      withCredentials: true,
      timeoutMs: REQUEST_TIMEOUT_MS,
      signal,
    }),
  );

  const rawItems = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response)
      ? response
      : [];

  return {
    items: rawItems.map(normalizeBookingRecord),
    count: Number.isFinite(Number(response?.count))
      ? Math.max(0, Number(response.count))
      : rawItems.length,
    nextCursor: norm(response?.nextCursor ?? response?.next_cursor) || null,
  };
}

export async function getBooking(bookingId, { signal } = {}) {
  const id = requiredText(
    bookingId,
    "Booking reference",
    "INVALID_BOOKING_ID",
  );

  const response = await apiRequest(
    `/bookings/${encodeURIComponent(id)}`,
    "GET",
    null,
    {
      requiresAuth: true,
      suppressAuthInvalidEvent: false,
      withCredentials: true,
      timeoutMs: REQUEST_TIMEOUT_MS,
      signal,
    },
  );

  return normalizeBookingRecord(response);
}

export async function updateBookingStatus(
  bookingId,
  status,
  { ownerNotes, signal } = {},
) {
  const id = requiredText(
    bookingId,
    "Booking reference",
    "INVALID_BOOKING_ID",
  );
  const nextStatus = normalizeStatus(status, { required: true });

  const body = { status: nextStatus };
  const notes = optionalText(
    ownerNotes,
    "Owner notes",
    "INVALID_OWNER_NOTES",
    MAX_NOTES_LENGTH,
  );

  if (ownerNotes !== undefined) {
    body.ownerNotes = notes;
  }

  const response = await apiRequest(
    `/bookings/${encodeURIComponent(id)}`,
    "PATCH",
    body,
    {
      requiresAuth: true,
      suppressAuthInvalidEvent: false,
      withCredentials: true,
      timeoutMs: REQUEST_TIMEOUT_MS,
      signal,
    },
  );

  return normalizeBookingRecord(response);
}

export const isBookingNotFoundError = (error) =>
  Number(error?.status) === 404;

export const isBookingConflictError = (error) =>
  Number(error?.status) === 409;

export const isBookingRateLimitError = (error) =>
  Number(error?.status) === 429;
